import { z } from 'zod';
import { Flow, isDanishFlowCountry, OVER_18_DENMARK_FLOW, OVER_18_FOREIGN_FLOW, UNDER_18_DENMARK_FLOW } from '../flow';
import {
  AddressInfoSchema,
  BankInfoSchema,
  BankInfoSchemaWithRequiredTin,
  ConfirmSchemaWithAcceptTerms,
  ContactInfoSchema,
  MemberSchema,
  MitIdSchema,
  PersonalInfoSchema,
} from './member';
import { validators } from './validators';

export type ValidationProfile = 'over18Denmark' | 'over18ForeignWithDanishCpr' | 'over18ForeignWithoutDanishCpr';

export type ValidationStepKey =
  | 'personal-info'
  | 'contact-info'
  | 'address-info'
  | 'bank-details'
  | 'mit-id'
  | 'confirm-info'
  | 'payment';

type ConditionalRequiredField = 'Cpr' | 'TIN';

const requiredConditionalFieldsByProfileAndStep: Record<
  ValidationProfile,
  Partial<Record<ValidationStepKey, ConditionalRequiredField[]>>
> = {
  over18Denmark: {
    'mit-id': ['Cpr'],
    payment: ['Cpr'],
  },
  over18ForeignWithDanishCpr: {
    'mit-id': ['Cpr'],
    payment: ['Cpr'],
  },
  over18ForeignWithoutDanishCpr: {
    'bank-details': ['TIN'],
    payment: ['TIN'],
  },
};

const getConditionalRequiredFields = (
  profile: ValidationProfile,
  stepKey: ValidationStepKey,
): ConditionalRequiredField[] => {
  return requiredConditionalFieldsByProfileAndStep[profile][stepKey] ?? [];
};

const validateConditionalRequiredFields = (
  requiredFields: ConditionalRequiredField[],
  data: { Cpr: string; TIN: string },
  ctx: z.RefinementCtx,
) => {
  if (requiredFields.includes('Cpr')) {
    const cprParseResult = validators.cpr().safeParse(data.Cpr);
    if (!cprParseResult.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['Cpr'],
        message: cprParseResult.error.issues[0]?.message,
      });
    }
  }

  if (requiredFields.includes('TIN')) {
    const tinParseResult = validators.tinRequired().safeParse(data.TIN);
    if (!tinParseResult.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['TIN'],
        message: tinParseResult.error.issues[0]?.message || 'member.validation.bank.tinRequired',
      });
    }
  }
};

type MemberValidationContext = {
  CountryCode: string;
  HasDanishCpr: boolean | null | undefined;
};

const PROFILE_BY_FLOW: Record<Exclude<Flow, typeof UNDER_18_DENMARK_FLOW>, ValidationProfile> = {
  [OVER_18_DENMARK_FLOW]: 'over18Denmark',
  [OVER_18_FOREIGN_FLOW]: 'over18ForeignWithoutDanishCpr',
};

const PROFILE_BY_FOREIGN_CPR: Record<'with' | 'without', ValidationProfile> = {
  with: 'over18ForeignWithDanishCpr',
  without: 'over18ForeignWithoutDanishCpr',
};

type ValidatorByStep = Record<ValidationStepKey, z.ZodTypeAny>;

const resolvePaymentRequiredFields = (profile: ValidationProfile, countryCode: string): ConditionalRequiredField[] => {
  const requiredFields = [...getConditionalRequiredFields(profile, 'payment')];

  // Greenland belongs to the Danish profile but still requires both identifiers.
  if (profile === 'over18Denmark' && countryCode === 'GL' && !requiredFields.includes('TIN')) {
    requiredFields.push('TIN');
  }

  return requiredFields;
};

const buildSubmitSchemaForProfile = (profile: ValidationProfile) =>
  MemberSchema.superRefine((data, ctx) => {
    const requiredFields = resolvePaymentRequiredFields(profile, data.CountryCode);
    validateConditionalRequiredFields(requiredFields, { Cpr: data.Cpr, TIN: data.TIN }, ctx);
  });

const submitMemberSchemaByProfile: Record<ValidationProfile, z.ZodTypeAny> = {
  over18Denmark: buildSubmitSchemaForProfile('over18Denmark'),
  over18ForeignWithDanishCpr: buildSubmitSchemaForProfile('over18ForeignWithDanishCpr'),
  over18ForeignWithoutDanishCpr: buildSubmitSchemaForProfile('over18ForeignWithoutDanishCpr'),
};

const getSubmitSchemaForProfile = (profile: ValidationProfile): z.ZodTypeAny => {
  return submitMemberSchemaByProfile[profile];
};

const stepValidatorsByProfile: Record<ValidationProfile, ValidatorByStep> = {
  over18Denmark: {
    'personal-info': PersonalInfoSchema,
    'contact-info': ContactInfoSchema,
    'address-info': AddressInfoSchema,
    'bank-details': getConditionalRequiredFields('over18Denmark', 'bank-details').includes('TIN')
      ? BankInfoSchemaWithRequiredTin
      : BankInfoSchema,
    'mit-id': MitIdSchema,
    'confirm-info': ConfirmSchemaWithAcceptTerms,
    payment: getSubmitSchemaForProfile('over18Denmark'),
  },
  over18ForeignWithDanishCpr: {
    'personal-info': PersonalInfoSchema,
    'contact-info': ContactInfoSchema,
    'address-info': AddressInfoSchema,
    'bank-details': getConditionalRequiredFields('over18ForeignWithDanishCpr', 'bank-details').includes('TIN')
      ? BankInfoSchemaWithRequiredTin
      : BankInfoSchema,
    'mit-id': MitIdSchema,
    'confirm-info': ConfirmSchemaWithAcceptTerms,
    payment: getSubmitSchemaForProfile('over18ForeignWithDanishCpr'),
  },
  over18ForeignWithoutDanishCpr: {
    'personal-info': PersonalInfoSchema,
    'contact-info': ContactInfoSchema,
    'address-info': AddressInfoSchema,
    'bank-details': getConditionalRequiredFields('over18ForeignWithoutDanishCpr', 'bank-details').includes('TIN')
      ? BankInfoSchemaWithRequiredTin
      : BankInfoSchema,
    'mit-id': MitIdSchema,
    'confirm-info': ConfirmSchemaWithAcceptTerms,
    payment: getSubmitSchemaForProfile('over18ForeignWithoutDanishCpr'),
  },
};

export const resolveValidationProfile = ({ CountryCode, HasDanishCpr }: MemberValidationContext): ValidationProfile => {
  if (isDanishFlowCountry(CountryCode)) {
    return 'over18Denmark';
  }

  return HasDanishCpr ? PROFILE_BY_FOREIGN_CPR.with : PROFILE_BY_FOREIGN_CPR.without;
};

const resolveValidationProfileFromFlow = (flow: Flow, hasDanishCpr: boolean | null | undefined): ValidationProfile => {
  if (flow === OVER_18_DENMARK_FLOW) {
    return PROFILE_BY_FLOW[OVER_18_DENMARK_FLOW];
  }

  return hasDanishCpr ? PROFILE_BY_FOREIGN_CPR.with : PROFILE_BY_FOREIGN_CPR.without;
};

export const getStepValidatorForMemberStep = (
  stepKey: ValidationStepKey,
  member: MemberValidationContext,
): z.ZodTypeAny => {
  const profile = resolveValidationProfile(member);
  return stepValidatorsByProfile[profile][stepKey];
};

export const getSubmitMemberSchema = (member: MemberValidationContext): z.ZodTypeAny => {
  const profile = resolveValidationProfile(member);
  return getSubmitSchemaForProfile(profile);
};

export const getStepValidatorForFlowStep = (
  stepKey: ValidationStepKey,
  flow: Flow,
  hasDanishCpr: boolean | null | undefined,
): z.ZodTypeAny => {
  const profile = resolveValidationProfileFromFlow(flow, hasDanishCpr);
  return stepValidatorsByProfile[profile][stepKey];
};
